import { useCallback, useEffect, useMemo, useState } from 'react'
import { NavigateFunction, useNavigate } from 'react-router'
import { Grid, List } from 'semantic-ui-react'

import { useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { getIsFetching, getFetchError } from '@/reducers/fetch'
import {
  fetchUserYearEndModuleStatuses,
  YearEndModuleType,
} from '@/features/year-end-module-status/yearEndModuleStatus.slice'
import { fetchAnnualTaxFilingsIfNeeded } from '@/features/taxes/annual-taxes/annualTaxFilings.slice'
import { getAnnualTaxFilingForYearSelector } from '@/features/taxes/annual-taxes/annualTaxFilings.selector'
import { getYearEndModuleStatusesByType } from '@/features/year-end-module-status/baseYearEndModuleStatus.selector'
import { useFinalReviewStepContext, useYearFromParams } from '../hooks'
import {
  userSubmitBookkeepingModule,
  FETCH_USER_SUBMIT_BOOKKEEPING_MODULE_KEY,
} from '../../end-of-year-review/actions'
import {
  Alert,
  Checkbox,
  GridRowColumn,
  Loader,
  Text,
} from '@/componentsV1/BaseComponents'
import { StepHeaders } from '@/features/bookkeeping/step-header'
import FinalReviewWrapper from '../final-review-wrapper'
import { Accordion, LinkCard } from '@/ui'
import { ImageBackgroundColors } from '@/styles'
import './styles.scss'

export interface ContentProps {
  year?: string
  notesAdded: boolean
  completedTQ: boolean
  optedOutOfTaxes: boolean
  navigate: NavigateFunction
  onComplete: () => Promise<void>
  completing: boolean
  errorMessage?: string | null
}

const getBulletList = (completedTQ: boolean, optedOutOfTaxes: boolean) => {
  if (completedTQ) {
    return (
      <List bulleted className="flex-column bulleted-list">
        <List.Item>
          You&apos;ve reviewed your income and expenses and have identified any
          transactions that require correction.
        </List.Item>
        <List.Item>
          Your finalized books will be automatically sent to your tax preparer{' '}
          along with your completed Tax Questionnaire.
        </List.Item>
      </List>
    )
  } else if (optedOutOfTaxes) {
    return (
      <List bulleted className="flex-column bulleted-list">
        <List.Item>
          You&apos;ve reviewed your income and expenses and have identified any
          transactions that require correction.
        </List.Item>
      </List>
    )
  } else {
    return (
      <List bulleted className="flex-column bulleted-list">
        <List.Item>
          You&apos;ve reviewed your income and expenses and have identified any
          transactions that require correction.
        </List.Item>
        <List.Item>
          Your finalized books will be automatically sent to your tax preparer{' '}
          after you complete my Tax Questionnaire.
        </List.Item>
      </List>
    )
  }
}

// Component for when no notes were added - sign off flow
const NoNotesContent = ({
  year,
  completedTQ,
  optedOutOfTaxes,
  isChecked,
  onAcknowledgeClick,
}: {
  year?: string
  completedTQ: boolean
  optedOutOfTaxes: boolean
  isChecked: boolean
  onAcknowledgeClick: () => void
}) => {
  return (
    <StepHeaders
      customColor="green"
      description={`If everything looks satisfactory, finalize your ${year} books by signing off below.`}
      imageSrc="https://heard-images.s3.amazonaws.com/assets/book_celebration.svg"
      imageAlt="bookkeeping"
      imageWidth={180}
      imageHeight={180}
      title="Sign off on your books"
    >
      <GridRowColumn>
        <div className="alert-footer">
          <Alert
            title="You acknowledge that:"
            style={{ width: '520px' }}
            customIcon="none"
            contentStyle={{ textAlign: 'left' }}
            type="acknowledge"
          >
            {getBulletList(completedTQ, optedOutOfTaxes)}
            <Checkbox
              id={'acknowledge'}
              label="I acknowledge the above and sign off"
              checked={isChecked}
              onClick={onAcknowledgeClick}
            />
          </Alert>
        </div>
      </GridRowColumn>
    </StepHeaders>
  )
}

// Component for when notes were added - send back to bookkeeping team
const NotesAddedContent = () => {
  return (
    <StepHeaders
      customColor="green"
      description="Ensure you've double-checked your transactions for any other errors. Then, submit your notes to complete."
      title="Ready to send back to bookkeeping team?"
    >
      <GridRowColumn>
        <div style={{ maxWidth: '600px', margin: '0 auto' }}>
          <Accordion type="single" collapsible variant="text" className="mb-6">
            <Accordion.Item value="approval">
              <Accordion.Trigger>
                Why is my approval required for updates?
              </Accordion.Trigger>
              <Accordion.Content>
                <p>
                  As the business owner, you have the most context about your
                  transactions. Your approval ensures that any corrections we
                  make align with your understanding of the business activity
                  and maintains the accuracy of your financial records.
                </p>
              </Accordion.Content>
            </Accordion.Item>
          </Accordion>

          <div className="mb-4">
            <h3
              style={{
                fontSize: '20px',
                fontWeight: 600,
                marginBottom: '16px',
                textAlign: 'center',
              }}
            >
              What to expect next
            </h3>
          </div>

          <div className="flex gap-4">
            <LinkCard
              className="w-[230px]"
              title={<Text as="h3">{'Wait for the bookkeeping team'}</Text>}
              description={
                <Text as="bodySm">
                  After you submit your notes, we&apos;ll review your feedback
                  and make any corrections.
                </Text>
              }
              imageSrc="https://heard-images.s3.amazonaws.com/assets/pencil.svg"
              imageAlt="Wait for team"
              imageColor={ImageBackgroundColors.YELLOW}
              imageWidth={80}
            />
            <LinkCard
              title={
                <Text as="h3">{'Look over your amended transactions'}</Text>
              }
              className="w-[230px]"
              description={
                <Text as="bodySm">
                  If something still needs correcting, send your books back for
                  revision again.
                </Text>
              }
              imageSrc="https://heard-images.s3.amazonaws.com/assets/note.svg"
              imageAlt="Review amendments"
              imageColor={ImageBackgroundColors.ORANGE}
              imageWidth={80}
            />
            <LinkCard
              title={<Text as="h3">{'Sign off and finalize your books'}</Text>}
              className="w-[230px]"
              description={
                <Text as="bodySm">
                  If everything is satisfactory, submit your sign-off to
                  finalize your books for taxes.{' '}
                  <span style={{ fontWeight: 'bold' }}>
                    This is required to continue with the annual tax process.
                  </span>
                </Text>
              }
              imageSrc="https://heard-images.s3.amazonaws.com/assets/circle-check-mark.svg"
              imageAlt="Finalize books"
              imageColor={ImageBackgroundColors.GREEN}
              imageWidth={80}
            />
          </div>
        </div>
      </GridRowColumn>
    </StepHeaders>
  )
}

export const Content = ({
  year,
  notesAdded,
  completedTQ,
  optedOutOfTaxes,
  navigate,
  onComplete,
  completing,
  errorMessage,
}: ContentProps) => {
  const [isChecked, setIsChecked] = useState(notesAdded || false)

  useEffect(() => {
    if (notesAdded) {
      setIsChecked(true)
    }
  }, [notesAdded])

  const onAcknowledgeClick = () => {
    setIsChecked(!isChecked)
  }

  return (
    <FinalReviewWrapper
      year={year}
      step={3}
      stepBack={() =>
        navigate(
          `/taxes/annual/review-and-close-books/${year}/final-review-expenses`
        )
      }
      stepForward={onComplete}
      saveAndExit={() => navigate('/taxes/annual')}
      canComplete={isChecked}
      completing={completing}
      isComplete
      completeButtonText={notesAdded ? 'Submit Notes' : undefined}
    >
      <Grid>
        {errorMessage && (
          <GridRowColumn>
            <Alert title="Error submitting books" type="error">
              {errorMessage}
            </Alert>
          </GridRowColumn>
        )}
        <GridRowColumn centerContent className="header-container">
          {notesAdded ? (
            <NotesAddedContent />
          ) : (
            <NoNotesContent
              year={year}
              completedTQ={completedTQ}
              optedOutOfTaxes={optedOutOfTaxes}
              isChecked={isChecked}
              onAcknowledgeClick={onAcknowledgeClick}
            />
          )}
        </GridRowColumn>
      </Grid>
    </FinalReviewWrapper>
  )
}

export const FinalSignOff = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const year = useYearFromParams()
  const { finalReviewContext } = useFinalReviewStepContext()
  const [loading, setLoading] = useState(true)

  const { optedOutAt } =
    useReselector(getAnnualTaxFilingForYearSelector, year) ?? {}
  const tqModule = useReselector(getYearEndModuleStatusesByType)[
    YearEndModuleType.taxQuestionnaire
  ]

  const notesAdded = useMemo(() => {
    return Boolean(finalReviewContext?.updatedTransactionIds?.length)
  }, [finalReviewContext])

  const isSubmitting = useReselector(
    getIsFetching,
    FETCH_USER_SUBMIT_BOOKKEEPING_MODULE_KEY
  )
  const submitError = useReselector(
    getFetchError,
    FETCH_USER_SUBMIT_BOOKKEEPING_MODULE_KEY
  )

  const onComplete = useCallback(async () => {
    const result = await dispatch(userSubmitBookkeepingModule())
    if (result) {
      navigate('/taxes/annual')
    }
  }, [dispatch, navigate])

  useEffect(() => {
    const fetch = async () => {
      await Promise.all([
        dispatch(fetchUserYearEndModuleStatuses()),
        dispatch(fetchAnnualTaxFilingsIfNeeded()),
      ])

      setLoading(false)
    }

    fetch()
  }, [dispatch, year])

  return loading ? (
    <Loader loading />
  ) : (
    <Content
      year={year}
      notesAdded={notesAdded}
      completedTQ={Boolean(tqModule?.completedAt)}
      optedOutOfTaxes={Boolean(optedOutAt)}
      navigate={navigate}
      onComplete={onComplete}
      completing={isSubmitting}
      errorMessage={submitError?.message}
    />
  )
}
